import { MoveRight } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'

import { useWindowLocation } from 'utils/windowLocation'

import { BankClosureStrategyInput } from './BankClosureStrategyInput'
import { PayFrequencyField, PayScheduleNameField } from './FormFields'
import { PayDateFields } from './PayDateFields'
import { PayDatePreview } from './PayDatePreview'
import { type BankClosureStrategy, type PayFrequency, PayFrequencyLabels, type PayScheduleFormInputs } from '../../types/payroll'
import { useCreatePaySchedule } from '../hooks/useCreatePaySchedule'
import { useGetSamplePayPeriods } from '../hooks/useGetSamplePayPeriods'
import { Button } from '../ui/button'

const ManualDeductionsOnboardingImpl = () => {
  const { assign } = useWindowLocation()

  const createPayScheduleMutation = useCreatePaySchedule()
  const currentYear = new Date().getFullYear()

  const {
    control,
    getValues,
    formState: { isSubmitting, errors, isValid },
    handleSubmit,
    register,
    setValue,
    watch,
    trigger
  } = useForm<PayScheduleFormInputs>({
    mode: 'all',
    defaultValues: {
      frequency: '' as PayFrequency,
      name: '',
      bankClosureStrategy: '' as BankClosureStrategy,
      firstDay: undefined,
      secondDay: undefined,
      examplePayDate: undefined
    }
  })

  const onSubmit = async (data: PayScheduleFormInputs) => {
    if (isValid) {
      createPayScheduleMutation.mutate({
        frequency: data.frequency,
        name: data.name,
        bank_closure_strategy: data.bankClosureStrategy,
        first_day: data.firstDay,
        second_day: data.secondDay,
        reference_pay_date: data.examplePayDate,
      }, {
        onSuccess: (json) => {
          window.segment?.track('employeronboarding_configure_deductions', {
            version: 'legacy',
          })
          assign(json.redirect_to)
        }
      })
    }
  }

  const setName = (name: PayFrequency, onChange: React.Dispatch<React.SetStateAction<string>>) => {
    setValue('name', `${PayFrequencyLabels[name]} (${currentYear})`)
    onChange(name)
  }

  const frequency = watch('frequency')

  const { data, isLoading, error } = useGetSamplePayPeriods({
    frequency,
    bank_closure_strategy: watch('bankClosureStrategy'),
    first_day: watch('firstDay'),
    second_day: watch('secondDay'),
    reference_pay_date: watch('examplePayDate'),
    isValid,
  })

  return (
    <div>
      <h1 className='h1 mb-4'>We need a bit more information about your payroll setup</h1>
      <div className='label-default font-medium text-ds-text-secondary mb-8'>
        <p>
          Thatch will create payroll deductions for easy upload to your system. We'll use the provided information to ensure your employees' deductions are correctly distributed across pay periods.
        </p>
        <br />
        <p>
          You will be able to edit and create additional schedules in your Settings dashboard page after onboarding.
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 max-w-lg mx-auto'>
        <PayFrequencyField
          control={control}
          error={errors.frequency}
          fieldId='frequency'
          customOnChange={setName}
        />

        <PayScheduleNameField
          control={control}
          error={errors.name}
          fieldId='name'
        />

        <PayDateFields
          frequency={frequency}
          control={control}
          register={register}
          errors={errors}
          watch={watch}
          trigger={trigger}
          getValues={getValues}
        />

        <BankClosureStrategyInput
          register={register}
          error={errors.bankClosureStrategy}
        />

        {isValid && (
          <PayDatePreview isLoading={isLoading} data={data} error={error?.message} />
        )}

        <Button
          type='submit'
          disabled={isSubmitting || !isValid}
          className='w-full mt-8'
        >
          {isSubmitting ? 'Submitting...' : <span>Continue<MoveRight className='inline ml-2' size={16} /></span>}
        </Button>
      </form>
    </div>
  )
}

const ManualDeductionsOnboarding = () => {
  return <ManualDeductionsOnboardingImpl />
}

export default ManualDeductionsOnboarding
